import { useEffect, useRef, useState } from "react";
import { Col, FormGroup, Input, Label, Row } from "reactstrap";
import Select from "react-select";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import sidebar from "../../utils/sidebar";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import FixedSave from "../../../../components/Actions/FixedSave";
import SelectCustomStyles from "../../../../utils/styles/reactSelect";
import countries, { countryCodeOptions } from "../../../../data/countries";
import { get, postForm } from "../../../../utils/helpers/api";
import { useTranslation } from "react-i18next";

const countryOptions = countries.map((c) => ({
  value: c.slug,
  label: c.name,
}));

function CreateUserPage() {
  const { t } = useTranslation();
  const { state } = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [rolesData, setRolesData] = useState([]);
  const [branchesData, setBranchesData] = useState([]);
  const [initLoading, setInitLoading] = useState(true);
  const [userData, setUserData] = useState({
    profile_picture: null,
    firstname: state?.userData?.firstname || "",
    lastname: state?.userData?.lastname || "",
    email: state?.userData?.email || "",
    country_code: state?.userData?.country_code || "+213",
    phone_number: state?.userData?.phone_number || "",
    password: "",
    country: state?.userData?.country || "",
    role_id: state?.userData?.role_id || null,
    branch_ids: state?.userData?.branch_ids || [],
  });

  useEffect(() => {
    initData();
  }, []);

  const initData = async () => {
    setInitLoading(true);
    const data = await get('/settings/users/create');
    if (data.success) {
      setRolesData(data.roles);
      setBranchesData(data.branches);
      setInitLoading(false);
    }
  };

  const set = (field, value) => setUserData((prev) => ({ ...prev, [field]: value }));

  const onAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    set("profile_picture", file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const saveUser = async () => {
    setSaveLoading(true);
    try {
      const formData = new FormData();
      Object.entries(userData).forEach(([k, v]) => {
        if (v === null || v === undefined) return;
        // FormData stringifies arrays, so append each entry under `key[]`
        // for Laravel to read it back as an array.
        if (Array.isArray(v)) {
          v.forEach((item) => formData.append(`${k}[]`, item));
        } else {
          formData.append(k, v);
        }
      });

      const url = id ? `/settings/users/update/${id}` : `/settings/users/store`;
      const data = await postForm(url, formData);

      if (data.success) {
        navigate("/settings/users");
      } else {
        toast.error(data.message || t('common.somethingWentWrong'));
      }
    } catch {
      toast.error(t('common.somethingWentWrong'));
    }
    setSaveLoading(false);
  };

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader
          breadcrumb={[t('settings.users'), id ? t('settings.editUser') : t('settings.newUser')]}
          onBack={() => navigate('/settings/users')}
        />

        <div className="subheader-page-content scrollable">
          {/* Profile Picture */}
          <div className="d-flex align-items-center mb-4 gap-3">
            <div
              onClick={() => fileRef.current.click()}
              style={{
                width: 90,
                height: 90,
                borderRadius: "50%",
                background: "#e9ecef",
                border: "2px dashed #ced4da",
                overflow: "hidden",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {avatarPreview ? (
                <img src={avatarPreview} alt="avatar" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              ) : (
                <span style={{ fontSize: 28, color: "#adb5bd" }}>&#128100;</span>
              )}
            </div>
            <div>
              <div className="fw-semibold mb-1">{t('profile.profilePicture')}</div>
              <small className="text-muted d-block mb-2">{t('profile.clickCircleToUpload')}</small>
              <input ref={fileRef} type="file" accept="image/*" className="d-none" onChange={onAvatarChange} />
            </div>
          </div>

          <Row>
            <Col md="6" className="mb-3">
              <Label>{t('profile.firstName')}</Label>
              <Input value={userData.firstname} onChange={(e) => set("firstname", e.target.value)} />
            </Col>
            <Col md="6" className="mb-3">
              <Label>{t('profile.lastName')}</Label>
              <Input value={userData.lastname} onChange={(e) => set("lastname", e.target.value)} />
            </Col>
          </Row>

          <Row>
            <Col md="6" className="mb-3">
              <Label>{t('common.email')}</Label>
              <Input type="email" value={userData.email} onChange={(e) => set("email", e.target.value)} />
            </Col>
            <Col md="6" className="mb-3">
              <Label>{t('settings.phoneNumber')}</Label>
              <div className="d-flex gap-2">
                <div style={{ width: 140, flexShrink: 0 }}>
                  <Select
                    options={countryCodeOptions}
                    value={countryCodeOptions.find((o) => o.value === userData.country_code) || null}
                    onChange={(selected) => set("country_code", selected ? selected.value : "")}
                    styles={SelectCustomStyles}
                  />
                </div>
                <Input
                  type="tel"
                  placeholder={t('common.phone')}
                  value={userData.phone_number}
                  onChange={(e) => set("phone_number", e.target.value)}
                />
              </div>
            </Col>
          </Row>

          <Row>
            <Col md="6" className="mb-3">
              <Label>{t('settings.password')}</Label>
              <Input type="password" value={userData.password} onChange={(e) => set("password", e.target.value)} />
            </Col>
            <Col md="6" className="mb-3">
              <Label>{t('common.country')}</Label>
              <Select
                options={countryOptions}
                value={countryOptions.find((o) => o.value === userData.country) || null}
                onChange={(selected) => set("country", selected ? selected.value : "")}
                styles={SelectCustomStyles}
              />
            </Col>
          </Row>

          <FormGroup>
            <Label>{t('settings.role')}</Label>
            <Select
              options={rolesData.map((r) => ({ value: r.id, label: r.name }))}
              styles={SelectCustomStyles}
              value={rolesData.map((r) => ({ value: r.id, label: r.name })).find((o) => o.value === userData.role_id) || null}
              onChange={(selected) => set("role_id", selected ? selected.value : null)}
            />
          </FormGroup>

          <FormGroup>
            <Label>{t('settings.branches')}</Label>
            <Select
              options={branchesData.map((b) => ({ value: b.id, label: b.name }))}
              value={branchesData.map((b) => ({ value: b.id, label: b.name })).filter((o) => userData.branch_ids.includes(o.value))}
              onChange={(selected) => set("branch_ids", selected ? selected.map((s) => s.value) : [])}
              styles={SelectCustomStyles}
              isMulti
            />
          </FormGroup>
        </div>
      </div>

      <FixedSave
        onSave={saveUser}
        loading={saveLoading}
        disabled={saveLoading}
      />
    </SidebarLayout>
  );
}

export default CreateUserPage;
